import type { HarmonyPackageDocument } from '@rhythmic/db/types'
import type { UseLiveQueryReturn } from '@tanstack/vue-db'
import { createHooks } from 'hookable'
import type { Ref } from 'vue'
import { computed, markRaw, ref, shallowRef, watch } from 'vue'
import { useDocumentById } from '../../queries'
import useDocumentContextOnContextChanged from './events/useDocumentContext.onContextChanged'
import useDocumentContextOnDocumentIdChanged from './events/useDocumentContext.onDocumentIdChanged'
import type { DocumentContextEvents } from './useDocumentContext.events.schema'

interface DocumentContextLoggerLike {
  debug: (message: string, meta?: Record<string, unknown> | unknown) => void
}

interface InitDocumentContextQueriesParams {
  logger: DocumentContextLoggerLike
  packageId: Readonly<Ref<string | undefined>>
}

function stopTargets(logger: DocumentContextLoggerLike, target: unknown, methods: string[]) {
  if (!target) return

  for (const method of methods) {
    const fn = (target as Record<string, unknown>)[method]
    if (typeof fn !== 'function') continue

    try {
      ;(fn as () => void).call(target)
    } catch (error) {
      logger.debug('Failed to invoke lifecycle method on document query target', { method, error })
    }
  }
}

/**
 * Document context query orchestrator with explicit, hook-driven updates.
 *
 * Route params are synced into `documentId` by `document-context-router.client`.
 * The resolved `packageId` (harmony plan id) is supplied by the harmony plan context store.
 *
 * When either identifier changes, the orchestrator emits `ContextChanged` and a handler
 * configures (or clears) the live document query accordingly.
 */
export default function useDocumentContextQueries() {
  type DocumentContextHookMap = {
    [K in keyof DocumentContextEvents]: (event: DocumentContextEvents[K]) => void | Promise<void>
  }

  const hooks = markRaw(createHooks<DocumentContextHookMap>())

  const documentId = ref<string | undefined>(undefined)
  const currentDocumentQuery = shallowRef<UseLiveQueryReturn<HarmonyPackageDocument> | undefined>(undefined)
  const document = computed<HarmonyPackageDocument | null>(() => currentDocumentQuery.value?.data.value[0] ?? null)
  const isLoading = computed<boolean>(() => currentDocumentQuery.value?.isLoading.value ?? false)

  let hasInitialized = false

  function clearDocumentScopedQueries() {
    stopTargets(loggerRef.value, currentDocumentQuery.value, ['stop', 'dispose', 'unsubscribe'])
    currentDocumentQuery.value = undefined
  }

  const loggerRef = shallowRef<DocumentContextLoggerLike>({
    debug: () => {},
  })

  function init({ logger, packageId }: InitDocumentContextQueriesParams) {
    if (hasInitialized) return

    hasInitialized = true
    loggerRef.value = logger

    useDocumentContextOnDocumentIdChanged({
      hooks,
      logger,
      clearDocumentScopedQueries,
    })

    useDocumentContextOnContextChanged({
      hooks,
      logger,
      currentDocumentQuery,
      clearDocumentScopedQueries,
      createDocumentQuery: (resolvedPackageId, resolvedDocumentId) =>
        useDocumentById(resolvedPackageId, resolvedDocumentId),
    })

    watch(
      [packageId, documentId],
      ([nextPackageId, nextDocumentId], [previousPackageId, previousDocumentId]) => {
        void emitContextChanged({
          packageId: nextPackageId,
          previousPackageId,
          documentId: nextDocumentId,
          previousDocumentId,
        })
      },
      { immediate: true },
    )
  }

  function onDocumentIdChanged(handler: DocumentContextHookMap['DocumentIdChanged']) {
    return hooks.hook('DocumentIdChanged', handler)
  }

  function onContextChanged(handler: DocumentContextHookMap['ContextChanged']) {
    return hooks.hook('ContextChanged', handler)
  }

  async function emitDocumentIdChanged(event: DocumentContextEvents['DocumentIdChanged']) {
    return hooks.callHook('DocumentIdChanged', event)
  }

  async function emitContextChanged(event: DocumentContextEvents['ContextChanged']) {
    return hooks.callHook('ContextChanged', event)
  }

  return {
    init,
    hooks,
    onDocumentIdChanged,
    onContextChanged,
    emitDocumentIdChanged,
    emitContextChanged,
    clearDocumentScopedQueries,
    documentId,
    currentDocumentQuery,
    document,
    isLoading,
  }
}
